import React, {
  useState,
  useRef,
  useEffect,
  useContext,
} from 'react';
import {
  string,
  bool,
  shape,
  node,
} from 'prop-types';

const LazyLoadImgContext = React.createContext();

const LazyLoadImgProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [intersectionObserver, setIntersectionObserver] = useState(null);
  useEffect(() => {
    if ('IntersectionObserver' in window) {
      const callback = (entries) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (target.isLoadImage() || !isIntersecting) return;
          target.setIsLoadImage(true);
        });
      };

      const options = {
        rootMargin: '0px 0px 250px 0px',
        threshold: 0.1,
      };

      setIntersectionObserver(new IntersectionObserver(callback, options));
    }

    setIsInitialized(true);
  }, []);

  return (
    <LazyLoadImgContext.Provider
      value={{ intersectionObserver, isInitialized }}
    >
      {children}
    </LazyLoadImgContext.Provider>
  );
};

LazyLoadImgProvider.propTypes = {
  children: node.isRequired,
};

const LazyLoadImg = ({
  src,
  alt,
  rounded,
  style,
  lazy,
  ...rest
}) => {
  const [isLoadImage, setIsLoadImage] = useState(false);
  const { intersectionObserver, isInitialized } = useContext(LazyLoadImgContext);
  const imgRef = useRef(null);
  useEffect(() => {
    if (intersectionObserver && imgRef.current && lazy) {
      imgRef.current.isLoadImage = () => isLoadImage;
      imgRef.current.setIsLoadImage = setIsLoadImage;
      intersectionObserver.observe(imgRef.current);
    }

    return () => {
      if (intersectionObserver && imgRef.current && lazy) {
        intersectionObserver.unobserve(imgRef.current);
      }
    };
  }, [intersectionObserver, lazy]);

  const finalIsLoadImage = !lazy || (isInitialized && (!intersectionObserver || isLoadImage));

  return (
    <img
      {...rest}
      ref={imgRef}
      alt={alt}
      src={finalIsLoadImage ? src : null}
      style={{
        ...style,
        borderRadius: rounded ? '50%' : 0,
      }}
      data-selector-type="lazy-load-img"
    />
  );
};

LazyLoadImg.propTypes = {
  src: string,
  alt: string,
  rounded: bool,
  style: shape({}),
  lazy: bool,
};

LazyLoadImg.defaultProps = {
  src: null,
  alt: null,
  style: {},
  rounded: false,
  lazy: true,
};

export default LazyLoadImg;
export {
  LazyLoadImgProvider,
};
